/**
 * Excel查看器主组件
 * 展示Excel数据，支持图片/视频显示和筛选
 */
import React, { useState, useEffect } from 'react';
import { Card, Table, message, Alert, Spin, Typography, Space, Tooltip } from 'antd';
import { FileExcelOutlined, EyeOutlined } from '@ant-design/icons';
import { getExcelPreview } from '../../services/api';
import ImageCell from './ImageCell';
import VideoCell from './VideoCell';
import FilterPanel from './FilterPanel';
import './ExcelViewer.css';

const { Text } = Typography;

// 解析 URL 映射格式
// 输入: "image_1:https://...,image_2:https://..."
// 输出: ["https://...", "https://..."]
const parseUrlMapping = (text) => {
  if (!text || text === 'None' || text === 'null') {
    return [];
  }

  const textStr = String(text).trim();

  // 如果是单个 URL（不包含逗号分隔的映射），直接返回
  if (textStr.startsWith('http://') || textStr.startsWith('https://')) {
    if (!textStr.includes(',')) {
      return [textStr];
    }
    // 检查是否是简单的逗号分隔 URL 列表（没有 key: 前缀）
    if (!textStr.match(/[a-zA-Z_]+\d*:https?:\/\//)) {
      return textStr.split(',').map(u => u.trim()).filter(u => u);
    }
  }

  // 解析映射格式: key1:url1,key2:url2
  const urls = [];
  // 使用正则分割，确保在 key:http 格式前分割
  const parts = textStr.split(/,(?=[a-zA-Z_]+\d*:https?:\/\/)/);

  for (const part of parts) {
    const match = part.match(/^[a-zA-Z_]+\d*:(https?:\/\/.+)$/);
    if (match) {
      urls.push(match[1].trim());
    } else if (part.startsWith('http://') || part.startsWith('https://')) {
      urls.push(part.trim());
    }
  }

  return urls;
};

const ExcelViewer = ({ taskId, filename }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [excelData, setExcelData] = useState(null);
  const [filters, setFilters] = useState({});

  // 加载Excel数据
  const loadExcelData = async () => {
    setLoading(true);
    setError(null);

    try {
      // 构建筛选参数
      let filterColumn = null;
      let filterValues = null;

      // 获取第一个有筛选值的列
      for (const [key, values] of Object.entries(filters)) {
        if (values && values.length > 0) {
          filterColumn = key;
          filterValues = values;
          break;
        }
      }

      const response = await getExcelPreview(taskId, filterColumn, filterValues);
      
      if (response.success) {
        setExcelData(response.data);
      } else {
        setError('加载Excel数据失败');
      }
    } catch (err) {
      console.error('加载Excel失败:', err);
      setError(err.response?.data?.detail || err.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  // 初始加载和筛选变化时重新加载
  useEffect(() => {
    if (taskId) {
      loadExcelData();
    }
  }, [taskId, filters]);

  // 处理筛选变化
  const handleFilterChange = (columnKey, values) => {
    setFilters(prev => ({
      ...prev,
      [columnKey]: values
    }));
  };

  // 清除所有筛选
  const handleClearFilters = () => {
    setFilters({});
  };

  // 根据列类型渲染单元格
  const renderCell = (text, record, column) => {
    if (column.type === 'image' || column.type === 'object') {
      const urls = parseUrlMapping(text);
      return <ImageCell urls={urls} />;
    }

    if (column.type === 'video') {
      const urls = parseUrlMapping(text);
      return <VideoCell urls={urls} />;
    }
    
    if (column.type === 'url') {
      return text ? (
        <a href={text} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
          {text.length > 30 ? text.substring(0, 30) + '...' : text}
        </a>
      ) : '-';
    }
    
    // 文本和枚举类型
    if (!text || text === 'None' || text === 'null') {
      return <span style={{ color: '#999' }}>-</span>;
    }
    
    // 文本类型：使用Tooltip显示完整内容，单元格内显示省略号
    if (column.type === 'text' || column.type === 'enum') {
      const textStr = String(text);
      return (
        <Tooltip title={textStr} placement="topLeft">
          <span style={{ 
            fontSize: 13,
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            wordBreak: 'break-all'
          }}>
            {textStr}
          </span>
        </Tooltip>
      );
    }
    
    return <span style={{ fontSize: 13 }}>{text}</span>;
  };

  // 构建表格列配置
  const tableColumns = excelData?.columns.map(col => {
    let columnConfig = {
      title: col.title,
      dataIndex: col.key,
      key: col.key,
      render: (text, record) => renderCell(text, record, col),
    };

    // 设置固定宽度
    if (col.type === 'image' || col.type === 'object') {
      columnConfig.width = 280;
    } else if (col.type === 'video') {
      columnConfig.width = 280;
    } else {
      // 文本列固定宽度
      columnConfig.width = 360;
    }

    // 文本列启用省略号和悬浮提示
    if (col.type === 'text' || col.type === 'url') {
      columnConfig.ellipsis = {
        showTitle: false, // 禁用默认的title提示
      };
    }

    return columnConfig;
  }) || [];

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: '#999' }}>加载Excel数据中...</p>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert
        message="加载失败"
        description={error}
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  }

  if (!excelData) {
    return null;
  }

  return (
    <div className="excel-viewer">
      {/* 标题和统计信息 */}
      <Card
        title={
          <Space>
            <FileExcelOutlined />
            <span>Excel 数据查看</span>
          </Space>
        }
        extra={
          <Space>
            <Text type="secondary">
              {filename}
            </Text>
            <Text type="secondary">
              {excelData.filtered !== undefined
                ? `筛选: ${excelData.filtered}/${excelData.total} 行`
                : `共 ${excelData.total} 行`}
            </Text>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        {/* 筛选面板 */}
        <FilterPanel
          columns={excelData.columns}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
        />

        {/* 数据表格 */}
        <Table
          columns={tableColumns}
          dataSource={excelData.rows}
          rowKey="_row_id"
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 行`,
            pageSizeOptions: ['20', '50', '100', '200'],
          }}
          scroll={{ 
            x: tableColumns.reduce((sum, col) => sum + (col.width || 200), 0)
          }}
          size="small"
          bordered
          tableLayout="fixed"
        />
      </Card>
    </div>
  );
};

export default ExcelViewer;

